import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface Category {
  id: string;
  name_de: string;
}

interface ParsedProduct {
  name_de: string;
  name_en: string;
  description_de: string;
  description_en: string;
  price: number;
  category_name: string;
  category_id?: string;
  image_url: string;
  popular: boolean;
  badge_de: string;
  badge_en: string;
  valid: boolean;
  error?: string;
}

interface MenuImportTabProps {
  categories: Category[];
  onImported: () => void;
}

const TEMPLATE_CSV = `name_de,name_en,description_de,description_en,price,category_name,image_url,popular,badge_de,badge_en
Margherita Pizza,Margherita Pizza,Klassische Pizza mit Tomaten und Mozzarella,Classic pizza with tomatoes and mozzarella,14.50,Pizza,,false,,
Döner Kebab,Doner Kebab,Frisches Fleisch mit Salat und Sauce,Fresh meat with salad and sauce,12.00,Kebab,,true,Beliebt,Popular
Cola 0.5L,Cola 0.5L,Erfrischungsgetränk,Refreshing drink,3.50,Getränke,,false,,`;

const MenuImportTab = ({ categories, onImported }: MenuImportTabProps) => {
  const [dragOver, setDragOver] = useState(false);
  const [parsed, setParsed] = useState<ParsedProduct[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const fileRef = useRef<HTMLInputElement>(null);

  const parseCSV = useCallback((text: string): ParsedProduct[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));

    return lines.slice(1).map((line) => {
      // Handle quoted fields with commas
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') { inQuotes = !inQuotes; }
        else if (line[i] === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
        else { current += line[i]; }
      }
      values.push(current.trim());

      const row: Record<string, string> = {};
      headers.forEach((h, i) => { row[h] = (values[i] || '').replace(/^"|"$/g, '').trim(); });

      const price = parseFloat(row.price || '0');
      const catName = row.category_name || '';
      const cat = categories.find((c) => c.name_de.toLowerCase() === catName.toLowerCase());

      const product: ParsedProduct = {
        name_de: row.name_de || '',
        name_en: row.name_en || '',
        description_de: row.description_de || '',
        description_en: row.description_en || '',
        price,
        category_name: catName,
        category_id: cat?.id,
        image_url: row.image_url || '',
        popular: row.popular?.toLowerCase() === 'true',
        badge_de: row.badge_de || '',
        badge_en: row.badge_en || '',
        valid: true,
      };

      if (!product.name_de) { product.valid = false; product.error = 'Ürün adı (name_de) zorunlu'; }
      else if (isNaN(price) || price <= 0) { product.valid = false; product.error = 'Geçersiz fiyat'; }
      else if (!product.category_id) { product.valid = false; product.error = `Kategori bulunamadı: "${catName}"`; }

      return product;
    }).filter((p) => p.name_de !== '');
  }, [categories]);

  const handleFile = useCallback((file: File) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'csv') {
      alert('Sadece CSV dosyası desteklenmektedir. Excel dosyasını önce CSV olarak kaydedin.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const products = parseCSV(text);
      setParsed(products);
      setStep('preview');
      setImportResult(null);
    };
    reader.readAsText(file, 'UTF-8');
  }, [parseCSV]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleImport = async () => {
    const validProducts = parsed.filter((p) => p.valid);
    if (validProducts.length === 0) return;

    setImporting(true);
    let success = 0;
    let failed = 0;

    for (const p of validProducts) {
      const { error } = await supabase.from('products').insert({
        name_de: p.name_de,
        name_en: p.name_en || p.name_de,
        description_de: p.description_de,
        description_en: p.description_en || p.description_de,
        price: p.price,
        category_id: p.category_id,
        image_url: p.image_url || null,
        popular: p.popular,
        badge_de: p.badge_de || null,
        badge_en: p.badge_en || null,
        active: true,
        sort_order: 0,
      });
      if (error) { failed++; } else { success++; }
    }

    setImporting(false);
    setImportResult({ success, failed });
    setStep('done');
    if (success > 0) onImported();
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'menu_sablonu.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const validCount = parsed.filter((p) => p.valid).length;
  const invalidCount = parsed.filter((p) => !p.valid).length;

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-bold text-gray-900">CSV ile Menü İçe Aktar</h3>
          <p className="text-sm text-gray-500 mt-0.5">Hazırladığınız CSV dosyasını yükleyerek ürünleri toplu ekleyin</p>
        </div>
        <button
          onClick={downloadTemplate}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:border-orange-300 hover:text-orange-600 transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-download-line"></i></div>
          Şablon İndir
        </button>
      </div>

      {/* Info box */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
        <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
          <i className="ri-information-line"></i> CSV Dosyası Nasıl Hazırlanır?
        </p>
        <ul className="text-xs text-amber-600 space-y-1 list-disc list-inside">
          <li>Excel'de hazırlayıp <strong>"CSV olarak kaydet"</strong> seçeneğiyle kaydedin</li>
          <li><strong>category_name</strong> alanı mevcut kategorilerden biriyle tam eşleşmeli</li>
          <li>Mevcut kategoriler: {categories.map((c) => <span key={c.id} className="font-semibold">"{c.name_de}"</span>).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, ', ', el], [])}</li>
          <li>Fiyat nokta ile yazılmalı: <strong>14.50</strong></li>
          <li>popular alanı: <strong>true</strong> veya <strong>false</strong></li>
        </ul>
      </div>

      {/* Upload area */}
      {step === 'upload' && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
            dragOver ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/30'
          }`}
        >
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-orange-100 text-orange-500 mx-auto mb-4">
            <i className="ri-file-excel-2-line text-3xl"></i>
          </div>
          <p className="text-gray-700 font-semibold mb-1">CSV dosyasını buraya sürükleyin</p>
          <p className="text-gray-400 text-sm">veya tıklayarak seçin</p>
          <p className="text-xs text-gray-300 mt-3">Sadece .csv formatı desteklenir</p>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
          />
        </div>
      )}

      {/* Preview */}
      {step === 'preview' && (
        <div className="flex flex-col gap-4">
          {/* Summary */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-50 border border-green-100 text-sm">
              <div className="w-4 h-4 flex items-center justify-center text-green-600"><i className="ri-checkbox-circle-line"></i></div>
              <span className="text-green-700 font-semibold">{validCount} ürün hazır</span>
            </div>
            {invalidCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-100 text-sm">
                <div className="w-4 h-4 flex items-center justify-center text-red-500"><i className="ri-error-warning-line"></i></div>
                <span className="text-red-600 font-semibold">{invalidCount} hatalı satır</span>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Durum</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ürün Adı</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Kategori</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Fiyat</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Hata</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {parsed.map((p, i) => (
                    <tr key={i} className={!p.valid ? 'bg-red-50/50' : ''}>
                      <td className="px-4 py-2.5">
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full ${p.valid ? 'text-green-500' : 'text-red-400'}`}>
                          <i className={p.valid ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'}></i>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{p.name_de || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500">{p.category_name || '—'}</td>
                      <td className="px-4 py-2.5 font-semibold text-gray-900">
                        {p.price > 0 ? `CHF ${p.price.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-red-500 text-xs">{p.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleImport}
              disabled={importing || validCount === 0}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
            >
              {importing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-cloud-line"></i></div>
              )}
              {importing ? 'İçe aktarılıyor...' : `${validCount} Ürünü İçe Aktar`}
            </button>
            <button
              onClick={() => { setParsed([]); setStep('upload'); }}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Done */}
      {step === 'done' && importResult && (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-green-100 text-green-500">
            <i className="ri-checkbox-circle-line text-4xl"></i>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900 mb-1">İçe aktarma tamamlandı!</p>
            <p className="text-gray-500 text-sm">
              <span className="text-green-600 font-semibold">{importResult.success} ürün</span> başarıyla eklendi
              {importResult.failed > 0 && <>, <span className="text-red-500 font-semibold">{importResult.failed} ürün</span> başarısız</>}
            </p>
          </div>
          <button
            onClick={() => { setParsed([]); setStep('upload'); setImportResult(null); }}
            className="px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
          >
            Yeni Dosya Yükle
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuImportTab;
